import { FieldValues, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { whereEq } from 'ramda';

import PermissionSection from 'components/permission/PermissionSection';
import { notify } from 'components/notification/notificationSlice';
import {
  getCountries,
  getSupportedCountries,
  hasPermission,
} from 'pages/dashboard/selectors';
import { UploadFile, File } from 'components/uploadFile';
import { PaperClipIcon } from 'components/icons';
import { useAppDispatch, useAppSelector } from 'hooks';
import { Input, Select, DatePicker, PostalCode } from 'components/Inputs';
import AddressState from 'components/AddressState';
import { CountryISOCodes } from 'common/countries';
import { Permissions } from 'entities/dashboard';
import { AcceptFormat } from 'common/files';
import Button, { ButtonSize, ButtonVariant } from 'components/Button';
import {
  getBusinessesAddress,
  getBusinessSignatory,
  getPersonInfo,
} from 'pages/Profile/selectors';
import {
  getCityLabel,
  getPostalCodeLabel,
  handleAddressOnSubmit,
} from 'utils/address';

import { getSignatoryDetails, getSignatorySaving } from '../../selectors';
import { saveSignatoryDetails, downloadSignatoryDocument } from './thunks';
import {
  getSignatoryDetailsSchema,
  getComparableValues,
  mapApiToForm,
  defaultSignatoryFileData,
  SignatoryFileData,
} from './utils';

type SignatoryDetailsFormProps = {
  onCancel: () => void;
  permission?: Permissions;
};

const SignatoryDetailsForm: React.FC<SignatoryDetailsFormProps> = ({
  onCancel,
  permission,
}) => {
  const { t } = useTranslation('dashboard');
  const dispatch = useAppDispatch();

  const countries = useAppSelector(getCountries);
  const supportedCountries = useAppSelector(getSupportedCountries);
  const savedData = useAppSelector(getSignatoryDetails);
  const saving = useAppSelector(getSignatorySaving);
  const signatory = useAppSelector(getBusinessSignatory);
  const person = useAppSelector(getPersonInfo);
  const { countryIsocode } = useAppSelector(getBusinessesAddress);
  const permissions = useAppSelector((state) =>
    hasPermission(state, permission),
  );

  const [fileData, setFileData] = useState<SignatoryFileData>(
    defaultSignatoryFileData,
  );
  const [uploadedFiles, setUploadedFiles] = useState<any>([]);
  const [fileProgress, setFileProgress] = useState<number>(0);

  const readOnly = Boolean(permission) && !permissions.edit;
  const defaultCountry = countryIsocode || CountryISOCodes.USA;

  const schema = useMemo(() => getSignatoryDetailsSchema(t), [t]);

  const {
    handleSubmit,
    register,
    setValue,
    getValues,
    watch,
    control,
    trigger,
    formState: { errors, isDirty, isSubmitted },
    reset,
  } = useForm<FieldValues>({
    defaultValues: mapApiToForm(savedData, defaultCountry, person),
    resolver: yupResolver(schema),
  });

  const values = watch();

  // the defaults the form was last reset to - used to detect user edits that
  // RHF's isDirty misses (Select writes via setValue without shouldDirty)
  const appliedDefaultsRef = useRef(
    mapApiToForm(savedData, defaultCountry, person),
  );

  // an explicit upload/delete must pin the file area - fileData/fileProgress
  // pass through a no-file/no-progress gap between renders (React 17 doesn't
  // batch the timeout updates), which would let the sync below revert it
  const fileTouchedRef = useRef(false);

  useEffect(() => {
    // a late fetch must not wipe values the user already started entering,
    // a file mid fake-progress (fileProgress > 0), or dropdown-only changes
    const userChangedValues = !whereEq(
      getComparableValues(getValues()),
      getComparableValues(appliedDefaultsRef.current),
    );

    if (
      isDirty ||
      fileData.file ||
      fileProgress > 0 ||
      fileTouchedRef.current ||
      userChangedValues
    ) {
      return;
    }

    if (savedData?.documentsBlobId) {
      setFileData(defaultSignatoryFileData);
      setUploadedFiles([
        {
          id: savedData.documentsBlobId,
          blobFileName: savedData.blobFileName,
          blobFileExtension: savedData.blobFileExtension,
          blobFileSize: savedData.blobFileSize,
          description: t('dashboard:signatory.details.uploadedTitle'),
          createdOn: savedData.modifiedOn || savedData.createdOn,
        },
      ]);
    }

    const nextDefaults = mapApiToForm(savedData, defaultCountry, person);

    appliedDefaultsRef.current = nextDefaults;
    reset(nextDefaults);
  }, [
    savedData,
    isDirty,
    fileData.file,
    fileProgress,
    getValues,
    reset,
    defaultCountry,
    person,
    t,
  ]);

  useEffect(() => {
    if (fileData.file) {
      setUploadedFiles([
        {
          id: 0,
          blobFileName: fileData.blobFileName,
          blobFileExtension: fileData.blobFileExtension,
          blobFileSize: fileData.blobFileSize,
          description: t('dashboard:signatory.details.uploadedTitle'),
        },
      ]);
    }
  }, [fileData, t]);

  const handleFileDelete = () => {
    fileTouchedRef.current = true;
    setFileData(defaultSignatoryFileData);
    setUploadedFiles([]);
  };

  const handleFileUpload = (files: FileList) => {
    handleFileDelete();

    const file = files[0];
    const fileName = file.name.split('.');

    setFileProgress(100);

    setTimeout(() => {
      // file first: clearing progress while no file is set would commit the
      // exact gap the sync effect treats as "nothing in flight"
      setFileData({
        blobFileSize: file.size,
        blobFileName: file.name,
        blobFileExtension: fileName[fileName.length - 1],
        file,
      });

      setFileProgress(0);
    }, 2000);
  };

  const handleFileDownload = (name: string) => {
    if (savedData?.documentsBlobId && !fileData.file) {
      dispatch(
        downloadSignatoryDocument({
          guid: savedData.documentsBlobId,
          fileName: name,
        }),
      );
    }
  };

  const hasFile = Boolean(fileData.file) || uploadedFiles.length > 0;
  const isAuthorized = Boolean(signatory);

  const pristine =
    !fileData.file &&
    Boolean(savedData) &&
    whereEq(
      getComparableValues(values),
      getComparableValues(mapApiToForm(savedData, defaultCountry, person)),
    );

  const canSave = hasFile && (!pristine || !isAuthorized);

  const onSave = async (fields) => {
    const data = handleAddressOnSubmit(fields);

    const result = await dispatch(saveSignatoryDetails({ data, fileData }));

    if (result.error) {
      dispatch(
        notify({
          variant: 'error',
          duration: 5000,
          message:
            result.payload?.stage === 'attestation'
              ? t('dashboard:signatory.details.authorizeError')
              : t('dashboard:signatory.details.saveError'),
        }),
      );
    }
  };

  const handleCountryOnChange = () => {
    if (isSubmitted) {
      trigger();
    }
  };

  const selectedCountry = supportedCountries.find(
    (country) => country.value === values.countryIsocode,
  );

  const selectedNationality = countries.find(
    (country) => country.value === values.nationalityCountryIsocode,
  );

  return (
    <form className="px-6 pt-4">
      <div className="border border-gray-200 rounded-md">
        <div className="bg-gray-50 border-b border-gray-200 rounded-t-md px-3 py-2 text-sm leading-5 font-semibold text-gray-900">
          {t('dashboard:signatory.details.personalTitle')}
        </div>

        <div className="p-4 flex flex-col gap-4">
          <div className="flex gap-4">
            <Input
              name="givenName1"
              label={t('common:fields.firstname.label')}
              readOnly={readOnly}
              className="w-full"
              register={register}
              error={errors?.givenName1}
            />
            <Input
              name="surnameFirst"
              label={t('common:fields.lastname.label')}
              readOnly={readOnly}
              className="w-full"
              register={register}
              error={errors?.surnameFirst}
            />
          </div>

          <DatePicker
            label={t('dashboard:signatory.details.dateOfBirth.label')}
            disabled={readOnly}
            name="dateOfBirth"
            className="w-full min-w-0"
            maxDate={new Date()}
            control={control}
            error={errors?.dateOfBirth}
            countryIsocode={values.nationalityCountryIsocode}
          />
          <Select
            label={t('dashboard:signatory.details.nationality.label')}
            disabled={readOnly}
            name="nationalityCountryIsocode"
            value={selectedNationality}
            className="w-full min-w-0"
            options={countries}
            register={register}
            setValue={setValue}
            withIcon
            error={errors?.nationalityCountryIsocode}
          />

          <Input
            name="idnumber"
            label={t('dashboard:signatory.details.governmentId.label')}
            placeholder={t(
              'dashboard:signatory.details.governmentId.placeholder',
            )}
            readOnly={readOnly}
            className="w-full"
            register={register}
            error={errors?.idnumber}
          />

          <UploadFile
            title={t('dashboard:signatory.details.uploadTitle')}
            text={t('dashboard:signatory.details.uploadSubTitle')}
            fileUploadText={t('dashboard:signatory.details.uploadHint')}
            acceptFormat={[
              AcceptFormat.pdf,
              AcceptFormat.msword,
              AcceptFormat.document,
            ]}
            validations={{ maxSize: 10 }}
            onUpload={handleFileUpload}
            uploadFiles={uploadedFiles}
            progressPercent={fileProgress}
            handleProgress={setFileProgress}
            disabled={readOnly}
            permission={permission}
            stacked
            buttonClass="flex w-full items-center justify-start border-2 border-gray-300 border-dashed text-left rounded-lg px-6 py-3 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            {({ handleSelect }) =>
              uploadedFiles.map((file) => (
                <File
                  key={file.id}
                  file={file}
                  onDelete={handleFileDelete}
                  onDownload={handleFileDownload}
                  handleSelect={handleSelect}
                  uploadButtonText={t('dashboard:signatory.details.reupload')}
                  icon={
                    <PaperClipIcon className="flex-shrink-0 text-gray-500" />
                  }
                  hideDate
                  hideDeleteButton
                  framed
                  showFileType
                  readOnly={readOnly}
                  permission={permission}
                />
              ))
            }
          </UploadFile>
        </div>
      </div>

      <div className="border border-gray-200 rounded-md mt-4">
        <div className="bg-gray-50 border-b border-gray-200 rounded-t-md px-3 py-2 text-sm leading-5 font-semibold text-gray-900">
          {t('dashboard:signatory.details.addressTitle')}
        </div>

        <div className="p-4 flex flex-col gap-4">
          <Select
            label={t('common:fields.country.label')}
            disabled={readOnly}
            name="countryIsocode"
            value={selectedCountry}
            className="w-full"
            options={supportedCountries}
            register={register}
            setValue={setValue}
            withIcon
            error={errors?.countryIsocode}
            onChange={handleCountryOnChange}
          />

          <Input
            name="addressLine1"
            label={t('common:fields.address1.label')}
            readOnly={readOnly}
            className="w-full"
            register={register}
            error={errors?.addressLine1}
          />

          <Input
            name="addressLine2"
            label={t('common:fields.address2.label')}
            readOnly={readOnly}
            className="w-full"
            register={register}
            optional
          />

          <Input
            name="city"
            label={t(getCityLabel(values.countryIsocode))}
            readOnly={readOnly}
            className="w-full"
            register={register}
            error={errors?.city}
          />

          <div className="flex gap-4">
            <AddressState
              label={t('common:fields.province.label')}
              name="stateOrProvince"
              className="w-full"
              disabled={readOnly}
              countryISOCode={values.countryIsocode}
              value={values.stateOrProvince}
              error={errors?.stateOrProvince}
              register={register}
              setValue={setValue}
            />
            <PostalCode
              name="postalCode"
              label={t(getPostalCodeLabel(values.countryIsocode))}
              countryIsocode={values.countryIsocode}
              setValue={setValue}
              shouldValidate={isSubmitted}
              readOnly={readOnly}
              className="w-full"
              register={register}
              error={errors?.postalCode}
            />
          </div>
        </div>
      </div>

      <div className="flex gap-6 justify-end mt-6 -mx-6 border-t border-gray-200 px-6 py-5">
        <Button
          type="button"
          variant={ButtonVariant.secondaryOutline}
          onClick={onCancel}
          size={ButtonSize.mediumLarge}
        >
          {t('common:cancel')}
        </Button>
        <PermissionSection permission={permission} showPopover edit>
          <Button
            type="button"
            onClick={handleSubmit(onSave)}
            disabled={!canSave || saving || readOnly}
            loading={saving}
            size={ButtonSize.mediumLarge}
          >
            {t('common:save')}
          </Button>
        </PermissionSection>
      </div>
    </form>
  );
};

export default SignatoryDetailsForm;
